function hasQuestionId(value) {
  return value !== undefined && value !== null && value !== '';
}

function getDocumentData(document) {
  if (!document || typeof document.data !== 'function') {
    return document;
  }

  return document.data();
}

function getQuestionId(question) {
  if (typeof question === 'string' || typeof question === 'number') {
    return question;
  }

  if (!question) {
    return null;
  }

  if (hasQuestionId(question.id)) {
    return question.id;
  }

  const data = getDocumentData(question);
  return data && hasQuestionId(data.id) ? data.id : null;
}

function toComparableId(id) {
  return String(id);
}

function hasValidSelectedOption(answer) {
  if (typeof answer === 'string' || typeof answer === 'number') {
    return true;
  }

  const data = getDocumentData(answer);
  return Boolean(
    data &&
    typeof data.selected_option === 'string' &&
    data.selected_option.trim() !== ''
  );
}

export function getAnsweredQuestionId(answer) {
  if (typeof answer === 'string' || typeof answer === 'number') {
    return answer;
  }

  if (!answer) {
    return null;
  }

  const data = getDocumentData(answer);

  if (data && hasQuestionId(data.question)) {
    return data.question;
  }

  if (hasQuestionId(answer.question)) {
    return answer.question;
  }

  if (hasQuestionId(answer.id)) {
    return answer.id;
  }

  return data && hasQuestionId(data.id) ? data.id : null;
}

export function normalizeAnsweredQuestions(answers) {
  if (!Array.isArray(answers)) {
    return [];
  }

  const normalized = [];
  const indexesByQuestionId = Object.create(null);

  answers.forEach((answer) => {
    const questionId = getAnsweredQuestionId(answer);
    if (!hasQuestionId(questionId) || !hasValidSelectedOption(answer)) {
      return;
    }

    const comparableId = toComparableId(questionId);
    if (Object.prototype.hasOwnProperty.call(indexesByQuestionId, comparableId)) {
      normalized[indexesByQuestionId[comparableId]] = answer;
      return;
    }

    indexesByQuestionId[comparableId] = normalized.length;
    normalized.push(answer);
  });

  return normalized;
}

export function upsertAnsweredQuestion(answers, answer) {
  return normalizeAnsweredQuestions((Array.isArray(answers) ? answers : []).concat(answer));
}

export function calculateEvaluationProgress(answers, questions) {
  if (!Array.isArray(questions) || questions.length === 0) {
    return 0;
  }

  const questionIds = Object.create(null);
  questions.forEach((question) => {
    const questionId = getQuestionId(question);
    if (hasQuestionId(questionId)) {
      questionIds[toComparableId(questionId)] = true;
    }
  });

  const totalQuestions = Object.keys(questionIds).length;
  if (totalQuestions === 0) {
    return 0;
  }

  const answeredQuestions = normalizeAnsweredQuestions(answers).filter((answer) => {
    const questionId = getAnsweredQuestionId(answer);
    return Object.prototype.hasOwnProperty.call(questionIds, toComparableId(questionId));
  }).length;

  const progress = Math.floor((answeredQuestions / totalQuestions) * 100);
  return Math.max(0, Math.min(100, progress));
}
